import { clockOffsetAt, type ShipTime } from '../clock-log.js';
import { DomainError } from '../errors.js';
import type { DownloadKind } from './download-plan.js';

/**
 * Gemi bazlı dosya ayarları ve indirme politikası (ADR 0028 #31, #40). Ayarlar şirket verisidir
 * (`vessel_file_settings`), ofiste değişir, yalnız o gemiye iner. Karar saf fonksiyondadır; gemi
 * sunucusunun indirme kuyruğu (M8a2) her turda sorar. Hız sınırı, kota ve disk kuralları ayrıdır
 * (`planDownloads`).
 */

export const BULK_MODES = ['window', 'always', 'manual'] as const;
export type BulkMode = (typeof BULK_MODES)[number];

export interface VesselFileSettings {
  /** Şirket dosyaları kotası, MB (#31). */
  filesQuotaMb: number;
  /** Bu boyut ve altı hemen iner, MB. */
  bulkThresholdMb: number;
  bulkMode: BulkMode;
  /** Gemi yerel saatiyle pencere, `HH:MM` (başlangıç dahil, bitiş hariç). */
  bulkWindowStart: string;
  bulkWindowEnd: string;
  /** Gemi yerel ayında toplu dosya sınırı, MB; 0 kapalı. */
  monthlyBulkCapMb: number;
  /** Hız sınırı, kbps; 0 sınırsız. */
  maxRateKbps: number;
}

export const DEFAULT_VESSEL_FILE_SETTINGS: VesselFileSettings = {
  filesQuotaMb: 20480,
  bulkThresholdMb: 50,
  bulkMode: 'window',
  bulkWindowStart: '00:00',
  bulkWindowEnd: '06:00',
  monthlyBulkCapMb: 5120,
  maxRateKbps: 0,
};

/** Bu boyutun üstündeki dosya her durumda pencereyi bekler (ADR 0028 EK). */
export const ALWAYS_BULK_ABOVE_BYTES = 256 * 1024 * 1024;

const MB = 1024 * 1024;
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

function nonNegativeInteger(value: unknown): boolean {
  return Number.isInteger(value) && (value as number) >= 0;
}

/**
 * Ayar aralıkları; ihlalde `VALIDATION_FAILED`. Pencerenin başlangıcı bitişine eşit olamaz: öyle
 * bir pencere hiç açılmaz ve toplu dosyalar sessizce hep bekler (her zaman için `always` kipi).
 */
export function validateVesselFileSettings(settings: VesselFileSettings): void {
  const ok =
    nonNegativeInteger(settings.filesQuotaMb) &&
    settings.filesQuotaMb > 0 &&
    nonNegativeInteger(settings.bulkThresholdMb) &&
    settings.bulkThresholdMb > 0 &&
    (BULK_MODES as readonly string[]).includes(settings.bulkMode) &&
    HHMM.test(settings.bulkWindowStart) &&
    HHMM.test(settings.bulkWindowEnd) &&
    settings.bulkWindowStart !== settings.bulkWindowEnd &&
    nonNegativeInteger(settings.monthlyBulkCapMb) &&
    nonNegativeInteger(settings.maxRateKbps);
  if (!ok) throw new DomainError('VALIDATION_FAILED', 'vessel_file_settings');
}

export interface BulkFile {
  size: number;
  kind: DownloadKind;
  /** Gemide önceki revizyonu doğrulanmış dokümanın yeni revizyonu. */
  isUpdate: boolean;
  /** Kullanıcı "Şimdi indir" dedi. */
  requested: boolean;
}

export type BulkDecision =
  | { allowed: true; class: 'immediate' | 'requested' | 'bulk' }
  | { allowed: false; reason: 'window' | 'monthly_cap' | 'manual' };

/** Anın gemi yerel saati `HH:MM`: dilimden ya da gemi saati kaydından (ADR 0065 §4). */
function localTime(nowUtc: string, time: ShipTime): string {
  if (typeof time !== 'string') {
    const at = Date.parse(nowUtc);
    return new Date(at + clockOffsetAt(time, at) * 60_000).toISOString().slice(11, 16);
  }
  const timeZone = time;
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(new Date(nowUtc));
  } catch {
    throw new DomainError('VALIDATION_FAILED', `time zone ${timeZone}`);
  }
}

function inWindow(time: string, start: string, end: string): boolean {
  return start <= end ? time >= start && time < end : time >= start || time < end;
}

/** Aktarımın yönü: buluttan gemiye indirme ya da gemiden buluta yükleme (ADR 0038 §14). */
export type TransferDirection = 'download' | 'upload';

export interface TransferFile {
  direction: TransferDirection;
  size: number;
  /** İndirmede dokümanın türü ya da sertifika sayfası; yüklemede kullanılmaz. */
  kind?: DownloadKind | null;
  /** İndirmede: gemide önceki revizyonu doğrulanmış dokümanın yeni revizyonu. */
  isUpdate?: boolean;
  /** Kullanıcı "Şimdi indir" ya da "Upload now" dedi. */
  requested: boolean;
}

/**
 * Dosya şimdi aktarılabilir mi; iki yönün ortak kararı (ADR 0028 #40, ADR 0038 §14). Toplu dosya
 * eşiği, gemi yerel saatli pencere, 256 MB üstünün her zaman toplu sayılması, `manual` kipi ve
 * **tek aylık sınır** iki yönde aynıdır; sayaç (`usage`) iki yönü birlikte sayar. Duraklatma ve
 * hız sınırı çağıranın işidir (hız her yöne ayrı).
 * 1. Hemen: eşik altı dosya. İndirmede ayrıca form ya da prosedür dosyası, sertifika sayfası ve
 *    gemide önceki revizyonu doğrulanmış dokümanın güncellemesi; pencere ve aylık sınır
 *    uygulanmaz [ISM 11.2.1, 11.2.3]. Yüklemede bu ayrıcalık yoktur: dosya gemide zaten vardır,
 *    ofisteki kopya yedek ve görünürlüktür; sırayı yükleme kuyruğu verir. 256 MB üstü dosya her
 *    durumda pencereyi bekler. İndirmede ayrıcalıklı dosya yine 1. maddededir: yalnız pencereyi
 *    bekler (`always` kipte beklemez), aylık sınır onu durdurmaz ve toplu kullanıma sayılmaz
 *    (`immediate`). `manual` kipte de pencereyle iner: yeni revizyon gelince eskisi kalkar, gemi
 *    güncel kopyasız kalmasın (en temkinli seçenek).
 * 2. "Şimdi indir" / "Upload now" pencereyi ve aylık sınırı aşar.
 * 3. Toplu: `manual` kipte yalnız elle; `window` kipte gemi saatiyle pencerede; aylık sınır
 *    dolunca ay sonuna kadar durur (0 kapalı).
 */
export function bulkTransferAllowed(
  nowUtc: string,
  timeZone: ShipTime,
  settings: VesselFileSettings,
  usage: { bulkBytesThisMonth: number },
  file: TransferFile,
): BulkDecision {
  const small = file.size <= settings.bulkThresholdMb * MB;
  // Sertifika sayfası form gibi hemen iner: denetimde istenir (ADR 0029 K34)
  const critical =
    file.direction === 'download' &&
    (file.kind === 'form' ||
      file.kind === 'procedure' ||
      file.kind === 'certificate_page' ||
      file.isUpdate === true);
  const large = file.size > ALWAYS_BULK_ABOVE_BYTES;
  if ((small || critical) && !large) return { allowed: true, class: 'immediate' };
  if (file.requested) return { allowed: true, class: 'requested' };
  if (critical) {
    const open =
      settings.bulkMode === 'always' ||
      inWindow(localTime(nowUtc, timeZone), settings.bulkWindowStart, settings.bulkWindowEnd);
    return open ? { allowed: true, class: 'immediate' } : { allowed: false, reason: 'window' };
  }
  if (settings.bulkMode === 'manual') return { allowed: false, reason: 'manual' };
  if (
    settings.bulkMode === 'window' &&
    !inWindow(localTime(nowUtc, timeZone), settings.bulkWindowStart, settings.bulkWindowEnd)
  ) {
    return { allowed: false, reason: 'window' };
  }
  if (
    settings.monthlyBulkCapMb > 0 &&
    usage.bulkBytesThisMonth + file.size > settings.monthlyBulkCapMb * MB
  ) {
    return { allowed: false, reason: 'monthly_cap' };
  }
  return { allowed: true, class: 'bulk' };
}

/** Dosya şimdi inebilir mi (ADR 0028 #40): `bulkTransferAllowed`'ın indirme yönü. */
export function bulkDownloadAllowed(
  nowUtc: string,
  timeZone: ShipTime,
  settings: VesselFileSettings,
  usage: { bulkBytesThisMonth: number },
  file: BulkFile,
): BulkDecision {
  return bulkTransferAllowed(nowUtc, timeZone, settings, usage, { ...file, direction: 'download' });
}
